import EventEmitter from 'events';
import { log } from '@ali/re-utils';
import { transformSchema, generateId, classifyItems } from './utils';
import REMOTE from './protocal/remote';
import URI from './protocal/uri';
import VALUE from './protocal/value';
import parser from '../store/parser';

const DataProtocol = {};

class DataPool {
  static registerProtocal(protocal, module) {
    DataProtocol[protocal] = module;
  }

  constructor(schema, type = 'offline', ...otherOptions) {
    this.emitter = new EventEmitter();
    this.variables = {};
    this.itemsMap = {};
    this.otherOptions = otherOptions;
    this.globalConfig = schema.globalConfig || {};
    this.schema = transformSchema(schema);
    this.type = type;
    this.preseted = {};

    const context = otherOptions[0];
    this.context = context;
    if (context && context.fn) {
      if (context.fn.revo) {
        const getRevo = context.fn.revo;
        const revo = getRevo(context);
        this.revo = revo;
        this.fetch = revo.fetch.bind(revo);
        this.setGlobal = revo.setGlobal.bind(revo);
      } else if (context.fn.fetch) {
        this.fetch = context.fn.fetch;
      }

      if (context.fn.DSPreset) {
        this.preseted = context.fn.DSPreset({
          dataPool: this,
          protocols: { VALUE, URI, REMOTE },
          generateId: generateId(),
        });
      }
    }
    this.setFromSchema(this.schema);
  }

  setFromSchema(schema) {
    if (!schema || typeof schema !== 'object') {
      this.items = {
        online: [],
        offline: [],
      };
      return {};
    }
    this.items = {
      offline: [],
      online: [],
    };
    Object.keys(this.items).forEach((key) => {
      const presetDataSource = this.preseted;
      const customDataSource = schema[key].map((config) => {
        config.global = this.globalConfig;
        if (config.protocal in DataProtocol) {
          // 用户数据源将覆盖预置数据源
          if (config.name in presetDataSource) delete presetDataSource[config.name];
          const options = [this, config, generateId(), ...this.otherOptions];
          const item = new DataProtocol[config.protocal](...options);
          this.itemsMap[item.getId()] = item;
          return item;
        }
        return null;
      }).filter(item => item !== null);
      // 合并预置数据源和用户数据源
      this.items[key] = Object.keys(presetDataSource)
        .map(k => presetDataSource[k]).concat(customDataSource);
    });
    return this.items;
  }

  clearCache(name) {
    if (name && this.storeEngine) {
      const atom = this.storeEngine.get(name);
      if (atom) {
        const enableEmitter = atom.disableEmitter();
        atom.clear();
        enableEmitter();
      }
    }
  }

  exec(name, params = {}, options = {}) {
    if (this.storeEngine.isInBatch()) {
      throw new Error('Please don\'t execute values in template.');
    }
    const item = this.getVariable(name);
    if (!('forceUpdate' in options)) {
      options.forceUpdate = true;
    }
    const i = item.execute(params, options);
    log('=====>', item.getName(), 'start execute(manual)');
    if (i && i.then) {
      return i.then(({ value }) => {
        this.variables[item.getName()] = item.getVariables();
        this.emitter.emit('data', this.variables, [name]);
        log('=====>', item.getName(), 'loaded');
        return value;
      });
    }
    this.variables[item.getName()] = item.getVariables();
    this.emitter.emit('data', this.variables, [name]);
    return i;
  }

  executeAsync(items, changed) {
    items.forEach((item) => {
      const i = item.execute();
      log('=====>', item.getName(), 'start execute(async)');
      if (i && i.then) {
        // 根据dp内部的变量变化，标记整个dp中的变化
        i.then(({ changed: dataChanged }) => {
          this.variables[item.getName()] = item.getVariables();
          if (dataChanged) {
            changed.push(item.getName());
          }
          this.emitter.emit('datapool.index.loaded', 1);
          log('=====>', item.getName(), 'loaded');
        }, () => {
          this.emitter.emit('datapool.index.loaded', 1);
        });
      } else {
        this.variables[item.getName()] = item.getVariables();
        this.emitter.emit('datapool.index.loaded', 1);
      }
    });
  }

  execute() {
    if (this.exeLock) {
      return null;
    }
    const executeTime = Date.now();
    const items = this.items[this.type].slice(0);
    const { asyncLoad, autoLoad, manualLoad } = classifyItems(items);
    let loadedLength = 0;
    const asyncLoadLength = asyncLoad.length;
    const autoLoadLength = autoLoad.length;
    this.executeTime = executeTime;
    const changed = [];
    return new Promise((resolve, reject) => {
      const unLoad = this.onLoaded((len) => {
        loadedLength += len;
        // 手动变量设置初始值
        (manualLoad || []).forEach((item) => {
          if (!this.variables.hasOwnProperty(item.name)) {
            this.variables[item.name] = undefined;
          }
        });
        if (loadedLength >= asyncLoadLength + autoLoadLength) {
          // 抛出变化时，添加变化的字段
          this.emitter.emit('data', this.variables, changed);
          resolve(this.variables, changed);
          unLoad();
        }
      });
      this.executeAsync(asyncLoad, changed);
      const next = () => {
        const current = autoLoad.shift();
        if (executeTime !== this.executeTime) {
          return;
        }
        if (current == null) {
          this.emitter.emit('datapool.index.loaded', autoLoadLength);
          return;
        }
        const i = current.execute();
        const name = current.getName();
        log('=====>', name, 'start execute(sync)');
        if (i && i.then) {
          i.then(({ changed: dataChanged }) => {
            this.variables[name] = current.getVariables();
            if (dataChanged) {
              changed.push(current.getName());
            }
            log('=====>', name, 'loaded');
            next();
          }, reject);
        } else if (i === false) {
          reject();
        } else {
          this.variables[name] = current.getVariables();
        }
      };
      next();
    });
  }

  prepare(storeEngine) {
    // todo: refactor base on storeEngine
    this.storeEngine = storeEngine;
    return this.execute();
  }

  getVariables(key) {
    console.warn('`dp.getVariables` is deprecated. Use `dp.getValue`.');
    return this.getValue(key);
  }

  getVariable(name) {
    return this.items[this.type].filter(item => item.getName() === name)[0];
  }

  /**
   * get value from datapool, will report as observed if in template processing
   * @param {string} key datapool variable name
   */
  getValue(key) {
    const that = this;
    const variables = {};
    Object.keys(that.variables).forEach((k) => {
      Object.defineProperty(variables, k, {
        get() {
          that.storeEngine.reportObserved(k);
          return that.variables[k];
        },
        enumerable: true,
      });
    });
    return key ? variables[key] : variables;
  }

  setValue(name, value) {
    if (this.storeEngine.isInBatch()) {
      throw new Error('Please don\'t set values in template.');
    }
    if (typeof name === 'object') {
      this.exeLock = true;
      Object.keys(name).forEach(key => this.setValue(key, name[key]));
      this.exeLock = false;
      return this.execute();
    }

    const variable = this.getVariable(name);
    if (variable && variable.setValue) {
      variable.setValue(value);
    }
    return this.execute();
  }

  appendActionsToData(data) {
    let action = this.context.getAction();
    action = action && action.actionsMap;
    Object.keys(action || {}).forEach((key) => {
      if (!(key in data)) {
        data[key] = (...args) => this.context.execActionForDataPool(key, this, ...args);
      }
    });
    // system actions
    if (this.context) {
      this.context.appendSysActions(data);
    }
  }

  /**
   * parse template in datapool variable declare, collect used variable.
   * @param {String} tpl string template, e.i. '${name}'
   * @param {Bool} forceToString deprecated
   * @param {Object} appendVariables variable names need append
   * @param {Object} tokens used variable name will be collected in
   */
  parse(tpl, forceToString, appendVariables = {}, tokens) {
    const usedToken = {};
    // only the datapool could be used in this tpl.
    const data = {
      ...appendVariables,
    };
    const variables = this.variables || {};
    Object.keys(variables)
      .forEach((key) => {
        if (!(key in data)) {
          Object.defineProperty(data, key, {
            get() {
              usedToken[key] = true;
              return variables[key];
            },
            enumerable: true,
          });
        }
      });
    // append actions to data
    this.appendActionsToData(data);
    // TODO: if the logic turns out too messy, just fill the token
    // with any value, to force update the datapool value to
    // evaluate every time.
    // get the store
    const store = this.context.getStore();
    // start batch won't cost too much, just start one
    // this will also restrict any setValue in DP.
    store.startBatch();
    // render the value
    const rendered = parser(tpl, data);
    // it's done, end the batch
    const usedVariables = store.endBatch();
    if (tokens) {
      // use both the tokens for this variable
      // though not useful now, it will be used when parse
      // datapool dependency graph
      Object.keys(usedToken)
        .concat(Object.keys(usedVariables))
        .forEach((token) => {
          tokens[token] = true;
        });
    }
    return rendered;
  }

  parseRecursion(obj, forceToString = false, appendVariables = {}, usedToken) {
    if (!obj) return obj;
    if (Array.isArray(obj)) {
      return obj.map(item => this.parseRecursion(item, forceToString, appendVariables, usedToken));
    }
    const t = typeof obj;
    if (t === 'object') {
      if (obj.type === 'variable') {
        if (obj.variable !== undefined && obj.variable !== '') {
          return this.parse(obj.variable, forceToString, appendVariables, usedToken);
        }
        return obj.value;
      }

      const out = {};
      Object.keys(obj).forEach((key) => {
        out[key] = this.parseRecursion(obj[key], forceToString, appendVariables, usedToken);
      });
      return out;
    }
    if (t === 'string') {
      return this.parse(obj, forceToString, appendVariables, usedToken);
    }
    return obj;
  }

  onData(func) {
    this.emitter.on('data', func);
    return () => {
      this.emitter.removeListener('data', func);
    };
  }

  onChange(func) {
    this.emitter.on('change', func);
    return () => {
      this.emitter.removeListener('change', func);
    };
  }

  onLoaded(func) {
    this.emitter.on('datapool.index.loaded', func);
    return () => {
      this.emitter.removeListener('datapool.index.loaded', func);
    };
  }
}

DataPool.registerProtocal('REMOTE', REMOTE);
DataPool.registerProtocal('URI', URI);
DataPool.registerProtocal('VALUE', VALUE);

export default DataPool;
